'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useViewport } from '../useViewport';
import { CarouselCoreProps, CarouselCoreExtended, CarouselDragState } from './types';
import {
  validateIndex,
  calculateDragDelta,
  updateVelocity,
  markDragSignificance,
  resetDragState,
  createDragEventListeners,
  createAutoAdvanceListeners,
} from './utils';

/**
 * Core carousel hook providing fundamental functionality shared across all carousel types
 * Handles: navigation, drag/swipe, auto-advance, keyboard controls, state management
 */
export const useCarouselCore = ({
  totalItems,
  initialIndex = 0,
  loop = true,
  onIndexChange,
  onSwipe,
  enableDrag = true,
  dragSensitivity = 1,
  autoAdvance = false,
  autoAdvanceInterval = 3000,
  isVisible = true,
}: CarouselCoreProps): CarouselCoreExtended => {
  // Core state
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoAdvance);

  // Refs
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoAdvanceRef = useRef<number | null>(null);
  const dragStateRef = useRef<CarouselDragState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    initialOffset: 0,
    velocity: 0,
    lastTime: 0,
    isMouseDrag: false,
  });
  const hasDraggedRef = useRef(false);
  const dragEndHandlerRef = useRef<(() => void) | null>(null);
  const dragMoveHandlerRef = useRef<((clientX: number, clientY: number) => void) | null>(null);
  const actualCurrentIndexRef = useRef(initialIndex);
  const visibleCardsRef = useRef(1); // Default to 1, can be updated by specialized hooks

  const { width } = useViewport();

  // Update index with validation and callback notification
  const updateIndex = useCallback(
    (newIndex: number, visibleCards: number = 1) => {
      const validIndex = validateIndex(newIndex, totalItems, loop, visibleCards);

      if (validIndex !== currentIndex) {
        actualCurrentIndexRef.current = validIndex; // Update ref immediately
        setCurrentIndex(validIndex);
        onIndexChange?.(validIndex);
      }
    },
    [currentIndex, totalItems, loop, onIndexChange]
  );

  // Navigation functions - these will be enhanced by specialized hooks
  const goToPrevious = useCallback(
    (visibleCards: number = 1) => {
      const newIndex = loop && currentIndex === 0 ? totalItems - visibleCards : currentIndex - 1;
      updateIndex(newIndex, visibleCards);
    },
    [currentIndex, totalItems, loop, updateIndex]
  );

  const goToNext = useCallback(
    (visibleCards: number = 1) => {
      const maxIndex = Math.max(0, totalItems - visibleCards);
      const newIndex = loop && currentIndex >= maxIndex ? 0 : currentIndex + 1;
      updateIndex(newIndex, visibleCards);
    },
    [currentIndex, totalItems, loop, updateIndex]
  );

  const goToIndex = useCallback(
    (index: number, visibleCards: number = 1) => {
      updateIndex(index, visibleCards);
    },
    [updateIndex]
  );

  // Auto-advance functionality
  const pause = useCallback(() => {
    setIsPlaying(false);
    if (autoAdvanceRef.current) {
      clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    if (!autoAdvance || !isVisible || totalItems <= 1) return;
    setIsPlaying(true);
  }, [autoAdvance, isVisible, totalItems]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  // Auto-advance effect
  useEffect(() => {
    if (!isPlaying || !autoAdvance || !isVisible || totalItems <= 1) {
      return;
    }

    autoAdvanceRef.current = window.setInterval(() => {
      setCurrentIndex((prev) => {
        const visibleCards = visibleCardsRef.current;
        const maxIndex = Math.max(0, totalItems - visibleCards);
        const newIndex = loop && prev >= maxIndex ? 0 : prev + 1;
        const validIndex = validateIndex(newIndex, totalItems, loop, visibleCards);
        onIndexChange?.(validIndex);
        return validIndex;
      });
    }, autoAdvanceInterval);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [isPlaying, autoAdvance, isVisible, autoAdvanceInterval, totalItems, loop, onIndexChange]);

  // Pause on hover/focus using centralized utility
  useEffect(() => {
    return createAutoAdvanceListeners({
      carouselElement: carouselRef.current,
      autoAdvance,
      onPause: pause,
      onResume: () => autoAdvance && play(),
    });
  }, [autoAdvance, pause, play]);

  // Drag functionality - will be enhanced by specialized hooks
  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging) return;

      // Check if there's a custom handler, otherwise use default behavior
      if (dragMoveHandlerRef.current) {
        dragMoveHandlerRef.current(clientX, clientY);
        return;
      }

      // Default behavior using shared utilities
      const dragDelta = calculateDragDelta(
        clientX,
        clientY,
        dragStateRef.current.startX,
        dragStateRef.current.startY,
        dragSensitivity
      );

      // Prevent vertical scrolling interference
      if (dragDelta.shouldPreventVerticalScroll) {
        return;
      }

      // Allow free dragging - specialized hooks will handle constraints
      const newOffset = dragDelta.deltaX;

      // Mark significant drag using shared utility
      markDragSignificance(newOffset, hasDraggedRef);

      // Update velocity using shared utility
      updateVelocity(dragStateRef, newOffset);

      setDragOffset(newOffset);
    },
    [isDragging, dragSensitivity]
  );

  // Drag end handler - to be customized by specialized hooks
  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    // Check if there's a custom handler, otherwise use default behavior
    if (dragEndHandlerRef.current) {
      dragEndHandlerRef.current();

      // Custom handlers are responsible for calling the cleanup
      // Don't reset state here - let the custom handler do it
    } else {
      // Default behavior - reset drag state using shared utility
      resetDragState(setDragOffset, setIsDragging, autoAdvance, play);
    }
  }, [isDragging, autoAdvance, play]);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!enableDrag) return;

      event.preventDefault();
      pause(); // Pause auto-advance during drag

      const rect = carouselRef.current?.getBoundingClientRect();
      if (!rect) return;

      dragStateRef.current.startX = event.clientX;
      dragStateRef.current.startY = event.clientY;
      dragStateRef.current.currentX = 0;
      dragStateRef.current.lastTime = Date.now();
      dragStateRef.current.isMouseDrag = true;
      dragStateRef.current.velocity = 0;
      hasDraggedRef.current = false;

      setIsDragging(true);
      setDragOffset(0);
    },
    [enableDrag, pause]
  );

  // Touch event handlers
  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (!enableDrag) return;

      pause(); // Pause auto-advance during drag

      const touch = event.touches[0];
      const rect = carouselRef.current?.getBoundingClientRect();
      if (!touch || !rect) return;

      dragStateRef.current.startX = touch.clientX;
      dragStateRef.current.startY = touch.clientY;
      dragStateRef.current.currentX = 0;
      dragStateRef.current.lastTime = Date.now();
      dragStateRef.current.isMouseDrag = false;
      dragStateRef.current.velocity = 0;
      hasDraggedRef.current = false;

      setIsDragging(true);
      setDragOffset(0);
    },
    [enableDrag, pause]
  );

  // Global drag event listeners using centralized utility
  useEffect(() => {
    if (!enableDrag) return;

    const preventClickAfterDrag = (event: Event) => {
      if (hasDraggedRef.current) {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
          hasDraggedRef.current = false;
        }, 0);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (isDragging && dragStateRef.current.isMouseDrag) {
        handleDragMove(event.clientX, event.clientY);
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (isDragging && dragStateRef.current.isMouseDrag) {
        if (hasDraggedRef.current) {
          event.preventDefault();
          event.stopPropagation();
        }
        handleDragEnd();
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (isDragging && !dragStateRef.current.isMouseDrag && event.touches.length > 0) {
        if (event.cancelable) {
          event.preventDefault();
        }
        const touch = event.touches[0];
        handleDragMove(touch.clientX, touch.clientY);
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (isDragging && !dragStateRef.current.isMouseDrag) {
        if (hasDraggedRef.current && event.cancelable) {
          event.preventDefault();
          event.stopPropagation();
        }
        handleDragEnd();
      }
    };

    return createDragEventListeners({
      isDragging,
      isMouseDrag: dragStateRef.current.isMouseDrag,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
      preventClickAfterDrag,
      carouselElement: carouselRef.current,
    });
  }, [isDragging, enableDrag, handleDragMove, handleDragEnd]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, visibleCards: number = 1) => {
      const maxIndex = loop ? totalItems - 1 : Math.max(0, totalItems - visibleCards);

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPrevious(visibleCards);
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNext(visibleCards);
          break;
        case 'Home':
          event.preventDefault();
          goToIndex(0, visibleCards);
          break;
        case 'End':
          event.preventDefault();
          goToIndex(maxIndex, visibleCards);
          break;
        case ' ':
          if (autoAdvance) {
            event.preventDefault();
            togglePlayPause();
          }
          break;
      }
    },
    [goToPrevious, goToNext, goToIndex, totalItems, loop, autoAdvance, togglePlayPause]
  );

  return {
    currentIndex,
    isDragging,
    dragOffset,
    isPlaying,
    goToPrevious,
    goToNext,
    goToIndex,
    pause,
    play,
    togglePlayPause,
    carouselRef,
    handleKeyDown,
    handleMouseDown,
    handleTouchStart,
    extension: {
      updateIndex,
      handleDragMove,
      handleDragEnd,
      dragStateRef,
      width,
      setDragOffset,
      setIsDragging,
      dragEndHandlerRef,
      dragMoveHandlerRef,
      actualCurrentIndexRef,
      hasDraggedRef,
      visibleCardsRef,
      onSwipe,
    },
  };
};
